import type { OptionLabelPair } from '../../../../components/RadioGroup';
import { createInfoTooltipAddon } from '../../../../components/RadioGroup';
import type { BooleanFlag } from '../../../../types/boolean-flag';
import { BOOLEAN_FLAGS } from '../../../../types/boolean-flag';
import {
  INVOICE_NUMBER_SYNC_STRATEGY,
  INVOICE_PAYMENT_STATUS_SYNC_STRATEGY,
  InvoiceNumberSyncStrategy,
  InvoicePaymentStatusSyncStrategy,
  TAX_EXPORT_STRATEGY,
  TaxExportStrategy,
} from '@vy/integrations-types';

export const AUTOMATICALLY_SYNC_INVOICES_OPTIONS: OptionLabelPair<BooleanFlag>[] = [
  { value: BOOLEAN_FLAGS.ENABLED, label: 'Automatically sync invoices upon approval (recommended)' },
  { value: BOOLEAN_FLAGS.DISABLED, label: 'Do not sync automatically' },
];

export const AUTOMATICALLY_EXPORT_CUSTOMERS_OPTIONS: OptionLabelPair<BooleanFlag>[] = [
  { value: BOOLEAN_FLAGS.ENABLED, label: 'Export customers automatically (recommended)' },
  { value: BOOLEAN_FLAGS.DISABLED, label: 'Manual match' },
];

export const INVOICE_NUMBER_SYNC_STRATEGY_OPTIONS: OptionLabelPair<InvoiceNumberSyncStrategy>[] = [
  {
    value: INVOICE_NUMBER_SYNC_STRATEGY.KEEP_SEPARATE,
    label: 'Keep Invoice Numbers Separate between NetSuite & Vayu',
  },
  {
    value: INVOICE_NUMBER_SYNC_STRATEGY.VAYU_TO_ERP,
    label: 'Generate invoice numbers in Vayu and sync to NetSuite',
    renderAddon: createInfoTooltipAddon(
      "To use Vayu invoice numbers in NetSuite, you must enable 'Allow Override' in NetSuite.",
      "In NS: Setup → Company → Auto-Generated Numbers → Document Numbers. Check 'Allow Override' for Invoice row and Save.",
    ),
  },
  {
    value: INVOICE_NUMBER_SYNC_STRATEGY.ERP_TO_VAYU,
    label: 'Use NetSuite invoice numbers and override Vayu numbers',
  },
];

export const INVOICE_PAYMENT_STATUS_SYNC_STRATEGY_OPTIONS: OptionLabelPair<InvoicePaymentStatusSyncStrategy>[] =
  [
    {
      value: INVOICE_PAYMENT_STATUS_SYNC_STRATEGY.KEEP_SEPARATE,
      label: "Don't sync invoices billing status with 3rd parties",
    },
    {
      value: INVOICE_PAYMENT_STATUS_SYNC_STRATEGY.ERP_TO_VAYU,
      label: 'Use NetSuite as the source of truth for invoice status in Vayu',
    },
    {
      value: INVOICE_PAYMENT_STATUS_SYNC_STRATEGY.VAYU_TO_ERP,
      label: 'Manage invoice payments and status in Vayu (synced to NetSuite)',
    },
  ];

export const SUMMARIZE_LINE_ITEMS_BY_PRODUCT_ID_OPTIONS: OptionLabelPair<BooleanFlag>[] = [
  { value: BOOLEAN_FLAGS.ENABLED, label: 'Consolidate all invoice line items into a single total line item' },
  { value: BOOLEAN_FLAGS.DISABLED, label: 'Keep line items separated' },
];

export const OVERRIDE_LINE_ITEM_QUANTITY_OPTIONS: OptionLabelPair<BooleanFlag>[] = [
  { value: BOOLEAN_FLAGS.ENABLED, label: "Don't detail quantity" },
  { value: BOOLEAN_FLAGS.DISABLED, label: 'Keep original line item quantity' },
];

export const TAX_EXPORT_STRATEGY_OPTIONS: OptionLabelPair<TaxExportStrategy>[] = [
  { value: TAX_EXPORT_STRATEGY.INCLUDE_IN_AMOUNT, label: 'Include tax in line item amounts' },
  { value: TAX_EXPORT_STRATEGY.SEPARATE_LINE_ITEM, label: 'Export tax as a separate line item' },
];

export const LINK_INVOICE_TO_SALES_ORDER_OPTIONS: OptionLabelPair<BooleanFlag>[] = [
  {
    value: BOOLEAN_FLAGS.ENABLED,
    label: 'Link invoice to its Sales Order in NetSuite',
    renderAddon: createInfoTooltipAddon(
      'For supporting this feature it requires 2 modifications at NetSuite:',
      '1. Setup > Accounting > Accounting Preferences > Order Management: Make sure "Invoice in Advance of Fulfillment" is enabled.\n2. Add Vayu\'s custcol_vayu_product_id customField.',
    ),
  },
  { value: BOOLEAN_FLAGS.DISABLED, label: 'Do not link invoice to Sales Order' },
];
